import type { ComponentProps, Dispatch, KeyboardEvent, SetStateAction } from 'react'
import type { DeliveryRouteContact } from '@/lib/services/delivery-route-contacts/delivery-route-contacts.types'
import type { DeliveryShare } from '@/lib/services/delivery-shares/delivery-shares.types'
import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import LoadingButton from '@/components/ui/loading-button'
import FieldInfo from '@/components/form/FieldInfo'
import {
  getDeliveryRouteDateSummary,
  getDeliveryRouteLocationLabel,
  getDeliveryShareErrorMessage,
} from '@/components/orders/delivery-share.utils'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Typography } from '@/components/ui/typography'
import { useAppForm } from '@/lib/form'
import {
  deliveryRouteContactFormSchema,
  toDeliveryRouteContactPayload,
} from '@/lib/services/delivery-route-contacts/delivery-route-contacts.schema'
import {
  useCreateDeliveryRouteContactMutation,
  useDeleteDeliveryRouteContactMutation,
  useDeliveryRouteContacts,
  useUpdateDeliveryRouteContactMutation,
} from '@/lib/services/delivery-route-contacts/delivery-route-contacts.query'
import { useSendDeliveryShareMutation } from '@/lib/services/delivery-shares/delivery-shares.query'
import { deliveryShareSendSchema } from '@/lib/services/delivery-shares/delivery-shares.schema'
import { formatPhone } from '@/hooks/use-phone-formatter'
import { getErrorMessage, readProperty } from '@/lib/utils'
import { Mail, MessageSquareText, Pencil, Plus, Send, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

const MANUAL_CONTACT_ID = 'manual'

const DEFAULT_VALUES = {
  channel: 'email',
  contact_id: MANUAL_CONTACT_ID,
  email: '',
  sms: '',
}

const EMPTY_CONTACT_DRAFT = {
  name: '',
  email: '',
  phone: '',
}

function getContactDestination(
  contact: DeliveryRouteContact | undefined,
  channel: string
): string | null | undefined {
  return channel === 'sms' ? contact?.phone : contact?.email
}

function getContactOptionName(contact: DeliveryRouteContact): string {
  const destinations = [contact.email, contact.phone ? formatPhone(contact.phone) : null]
    .filter(Boolean)
    .join(' / ')

  return destinations ? `${contact.name} - ${destinations}` : contact.name
}

function useRouteShareForm(
  deliveryRouteContacts: DeliveryRouteContact[],
  sendDeliveryShare: ReturnType<typeof useSendDeliveryShareMutation>,
  share: DeliveryShare,
  setOpen: Dispatch<SetStateAction<boolean>>
) {
  return useAppForm({
    defaultValues: DEFAULT_VALUES,
    validators: {
      onSubmitAsync: async ({ value, formApi }) => {
        const selectedContact = deliveryRouteContacts.find(
          (contact) => String(contact.id) === String(value.contact_id)
        )
        const isManualEntry = value.contact_id === MANUAL_CONTACT_ID || !selectedContact
        const activeFieldName = value.channel === 'sms' ? 'sms' : 'email'
        const manualDestination = String(value[activeFieldName] || '').trim()

        try {
          if (isManualEntry && !manualDestination) {
            return {
              fields: {
                [activeFieldName]: [
                  value.channel === 'sms'
                    ? 'Phone number is required.'
                    : 'Email address is required.',
                ],
              },
            }
          }

          const payload = isManualEntry
            ? deliveryShareSendSchema.parse({
                channel: value.channel,
                destination: manualDestination,
              })
            : deliveryShareSendSchema.parse({
                channel: value.channel,
                delivery_route_contact_id: selectedContact.id,
              })

          if (!isManualEntry && !getContactDestination(selectedContact, value.channel)) {
            return {
              fields: {
                contact_id: [
                  value.channel === 'sms'
                    ? 'This contact does not have a phone number.'
                    : 'This contact does not have an email address.',
                ],
              },
            }
          }

          await sendDeliveryShare.mutateAsync({
            deliveryShareId: share.id,
            ...payload,
          })

          toast.success('Route link sent')
          formApi.reset()
          setOpen(false)
        } catch (error) {
          const fieldErrors = readProperty(
            readProperty(readProperty(error, 'response'), 'data'),
            'errors'
          )
          const destinationErrors = readProperty(fieldErrors, 'destination')

          if (destinationErrors) {
            return {
              fields: {
                [activeFieldName]: destinationErrors,
              },
            }
          }

          if (readProperty(fieldErrors, 'delivery_route_contact_id')) {
            return {
              fields: {
                contact_id: readProperty(fieldErrors, 'delivery_route_contact_id'),
              },
            }
          }

          if (readProperty(fieldErrors, 'channel')) {
            return {
              fields: {
                channel: readProperty(fieldErrors, 'channel'),
              },
            }
          }

          toast.error('Unable to send route link', {
            description: getDeliveryShareErrorMessage(error),
          })
        }

        return undefined
      },
    },
  })
}

export default function DeliveryRouteShareDialog({
  share,
  disabled = false,
  buttonVariant = 'outline',
  buttonSize,
  className = '',
}: {
  share: DeliveryShare
  disabled?: boolean
  buttonVariant?: ComponentProps<typeof Button>['variant']
  buttonSize?: ComponentProps<typeof Button>['size']
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const [editingContactId, setEditingContactId] = useState<number | null>(null)
  const [isContactFormOpen, setIsContactFormOpen] = useState(false)
  const sendDeliveryShare = useSendDeliveryShareMutation()
  const createContact = useCreateDeliveryRouteContactMutation()
  const updateContact = useUpdateDeliveryRouteContactMutation()
  const deleteContact = useDeleteDeliveryRouteContactMutation()
  const contactForm = useAppForm({
    defaultValues: EMPTY_CONTACT_DRAFT,
  })
  const { data: deliveryRouteContacts = [] } = useDeliveryRouteContacts({
    enabled: open,
  })

  const contactOptions = [
    { id: MANUAL_CONTACT_ID, name: 'Manual entry' },
    ...deliveryRouteContacts.map((contact) => ({
      id: contact.id,
      name: getContactOptionName(contact),
    })),
  ]

  const form = useRouteShareForm(deliveryRouteContacts, sendDeliveryShare, share, setOpen)

  const resetContactDraft = () => {
    contactForm.reset(EMPTY_CONTACT_DRAFT)
    setEditingContactId(null)
    setIsContactFormOpen(false)
  }

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)

    if (!nextOpen) {
      form.reset(DEFAULT_VALUES)
      resetContactDraft()
    }
  }

  const handleAddContact = () => {
    contactForm.reset(EMPTY_CONTACT_DRAFT)
    setEditingContactId(null)
    setIsContactFormOpen(true)
  }

  const handleEditContact = (contact: DeliveryRouteContact) => {
    contactForm.reset({
      name: contact.name || '',
      email: contact.email || '',
      phone: contact.phone || '',
    })
    setEditingContactId(contact.id)
    setIsContactFormOpen(true)
  }

  const handleSaveContact = async () => {
    try {
      const payload = toDeliveryRouteContactPayload(
        deliveryRouteContactFormSchema.parse({
          name: contactForm.getFieldValue('name'),
          email: contactForm.getFieldValue('email'),
          phone: contactForm.getFieldValue('phone'),
        })
      )
      const response = editingContactId
        ? await updateContact.mutateAsync({
            deliveryRouteContactId: editingContactId,
            ...payload,
          })
        : await createContact.mutateAsync(payload)
      const nextContactId =
        response?.id || readProperty(readProperty(response, 'data'), 'id') || editingContactId

      if (nextContactId) {
        // oxlint-disable-next-line typescript/no-base-to-string -- retain legacy id coercion for the optional wrapped-response fallback
        form.setFieldValue('contact_id', String(nextContactId))
      }

      toast.success(editingContactId ? 'Contact updated' : 'Contact saved')
      resetContactDraft()
    } catch (error) {
      toast.error('Unable to save contact', {
        description: getErrorMessage(error),
      })
    }
  }

  const isSavingContact = createContact.isPending || updateContact.isPending

  const handleContactDraftKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (
      event.key !== 'Enter' ||
      event.shiftKey ||
      event.metaKey ||
      event.ctrlKey ||
      event.altKey ||
      !(event.target instanceof HTMLInputElement)
    ) {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    if (!isSavingContact) {
      void handleSaveContact()
    }
  }

  const handleDeleteContact = async (contactId: number) => {
    try {
      await deleteContact.mutateAsync(contactId)

      if (String(form.getFieldValue('contact_id')) === String(contactId)) {
        form.setFieldValue('contact_id', MANUAL_CONTACT_ID)
      }

      resetContactDraft()
      toast.success('Contact deleted')
    } catch (error) {
      toast.error('Unable to delete contact', {
        description: getErrorMessage(error),
      })
    }
  }

  const stopCount = Number(share?.stop_count || 0)

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant={buttonVariant}
          size={buttonSize}
          className={className}
          disabled={disabled || !share?.share_url}
        >
          <Send className="size-4" />
          Share
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Share delivery route</DialogTitle>
          <DialogDescription>
            Send the secure driver link by email or text message.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border bg-muted/10 p-4">
          <Typography variant="label" className="block">
            Route summary
          </Typography>
          <Typography variant="supporting" className="mt-1 block">
            {getDeliveryRouteDateSummary(share)}
          </Typography>
          <Typography variant="supporting" className="block">
            {stopCount} stop{stopCount === 1 ? '' : 's'} -{' '}
            {getDeliveryRouteLocationLabel(share.location)}
          </Typography>
        </div>

        <form.AppForm>
          <form.Form>
            <div className="space-y-4">
              <form.AppField name="channel">
                {(field) => (
                  <>
                    <Tabs value={field.state.value} onValueChange={field.handleChange}>
                      <TabsList layout="fill">
                        <TabsTrigger value="email">
                          <Mail className="size-4" />
                          Email
                        </TabsTrigger>
                        <TabsTrigger value="sms">
                          <MessageSquareText className="size-4" />
                          Text Message
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="email" className="pt-4">
                        <DestinationFields
                          channel="email"
                          form={form}
                          contactOptions={contactOptions}
                          contacts={deliveryRouteContacts}
                          isContactFormOpen={isContactFormOpen}
                          onAddContact={handleAddContact}
                          onDeleteContact={handleDeleteContact}
                          onEditContact={handleEditContact}
                          deleteContactPending={deleteContact.isPending}
                        />
                      </TabsContent>

                      <TabsContent value="sms" className="pt-4">
                        <DestinationFields
                          channel="sms"
                          form={form}
                          contactOptions={contactOptions}
                          contacts={deliveryRouteContacts}
                          isContactFormOpen={isContactFormOpen}
                          onAddContact={handleAddContact}
                          onDeleteContact={handleDeleteContact}
                          onEditContact={handleEditContact}
                          deleteContactPending={deleteContact.isPending}
                        />
                      </TabsContent>
                    </Tabs>
                    <FieldInfo className="mt-2" />
                  </>
                )}
              </form.AppField>

              {isContactFormOpen ? (
                <contactForm.AppForm>
                  <div
                    className="space-y-3 rounded-lg border bg-background p-4"
                    onKeyDown={handleContactDraftKeyDown}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <Typography variant="label">
                        {editingContactId ? 'Edit contact' : 'Add contact'}
                      </Typography>
                      <Button variant="ghost" size="sm" type="button" onClick={resetContactDraft}>
                        Cancel
                      </Button>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <contactForm.AppField name="name">
                          {(nameField) => (
                            <nameField.TextInput id="delivery-route-contact-name" label="Name" />
                          )}
                        </contactForm.AppField>
                      </div>
                      <contactForm.AppField name="email">
                        {(emailField) => (
                          <emailField.EmailInput id="delivery-route-contact-email" label="Email" />
                        )}
                      </contactForm.AppField>
                      <contactForm.AppField name="phone">
                        {(phoneField) => (
                          <phoneField.PhoneInput id="delivery-route-contact-phone" label="Phone" />
                        )}
                      </contactForm.AppField>
                    </div>
                    <div className="flex justify-end">
                      <LoadingButton
                        type="button"
                        size="sm"
                        isPending={isSavingContact}
                        onClick={handleSaveContact}
                      >
                        {editingContactId ? 'Update contact' : 'Save contact'}
                      </LoadingButton>
                    </div>
                  </div>
                </contactForm.AppForm>
              ) : null}

              <Typography variant="supporting">
                We'll send the florist name, route summary, and secure delivery link.
              </Typography>

              <DialogFooter className="pt-2">
                <Button variant="outline" type="button" onClick={() => handleOpenChange(false)}>
                  Cancel
                </Button>
                <form.Subscribe selector={(state) => state.values.channel}>
                  {(channel) => (
                    <LoadingButton
                      isPending={sendDeliveryShare.isPending}
                      disabled={isContactFormOpen}
                      type="submit"
                    >
                      <Send className="size-4" />
                      {channel === 'sms' ? 'Send Text' : 'Send Email'}
                    </LoadingButton>
                  )}
                </form.Subscribe>
              </DialogFooter>
            </div>
          </form.Form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  )
}

function DestinationFields({
  channel,
  form,
  contactOptions,
  contacts,
  isContactFormOpen,
  onAddContact,
  onDeleteContact,
  onEditContact,
  deleteContactPending,
}: {
  channel: string
  form: ReturnType<typeof useRouteShareForm>
  contactOptions: { id: string | number; name: string }[]
  contacts: DeliveryRouteContact[]
  isContactFormOpen: boolean
  onAddContact: () => void
  onDeleteContact: (contactId: number) => Promise<void>
  onEditContact: (contact: DeliveryRouteContact) => void
  deleteContactPending: boolean
}) {
  return (
    <div className="space-y-4">
      <form.AppField name="contact_id">
        {(contactField) => {
          const selectedContact = contacts.find(
            (contact) => String(contact.id) === String(contactField.state.value)
          )
          const isManualEntry = contactField.state.value === MANUAL_CONTACT_ID || !selectedContact
          const selectedDestination = getContactDestination(selectedContact, channel)

          return (
            <div className="space-y-3">
              <contactField.SelectInput
                label="Saved contact"
                options={contactOptions}
                placeholder="Choose a saved contact"
              />
              {!isManualEntry ? (
                <div className="rounded-lg border bg-muted/10 p-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <Typography variant="label" className="block">
                        {selectedContact.name}
                      </Typography>
                      {selectedContact.email ? (
                        <Typography variant="supporting" className="block break-words">
                          {selectedContact.email}
                        </Typography>
                      ) : null}
                      {selectedContact.phone ? (
                        <Typography variant="supporting" className="block">
                          {formatPhone(selectedContact.phone)}
                        </Typography>
                      ) : null}
                      {!selectedDestination ? (
                        <Typography variant="label" tone="destructive" className="mt-2 block">
                          {channel === 'sms'
                            ? 'This contact needs a phone number before texting.'
                            : 'This contact needs an email address before emailing.'}
                        </Typography>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => onEditContact(selectedContact)}
                        aria-label={`Edit ${selectedContact.name}`}
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <ConfirmationDialog
                        title="Delete contact?"
                        description="This removes the saved delivery route contact. Existing route links are not affected."
                        confirmText="Delete"
                        pendingText="Deleting..."
                        isPending={deleteContactPending}
                        onConfirm={() => onDeleteContact(selectedContact.id)}
                        trigger={
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            aria-label={`Delete ${selectedContact.name}`}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        }
                      />
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          )
        }}
      </form.AppField>

      {!isContactFormOpen ? (
        <Button type="button" variant="outline" size="sm" onClick={onAddContact}>
          <Plus className="size-4" />
          Add contact
        </Button>
      ) : null}

      {!isContactFormOpen ? (
        <form.Subscribe selector={(state) => state.values.contact_id}>
          {(contactId) => {
            const selectedContact = contacts.find(
              (contact) => String(contact.id) === String(contactId)
            )
            const isManualEntry = contactId === MANUAL_CONTACT_ID || !selectedContact

            return isManualEntry ? (
              channel === 'email' ? (
                <form.AppField name="email">
                  {(emailField) => (
                    <emailField.EmailInput label="Email address" placeholder="driver@example.com" />
                  )}
                </form.AppField>
              ) : (
                <form.AppField name="sms">
                  {(smsField) => (
                    <smsField.PhoneInput label="Phone number" placeholder="(555) 555-5555" />
                  )}
                </form.AppField>
              )
            ) : null
          }}
        </form.Subscribe>
      ) : null}
    </div>
  )
}
